import React, { useEffect } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useAnnounce } from '../hooks/useAccessibility';

export const AriaAnnouncer: React.FC = () => {
  const announce = useAnnounce();
  const { currentTool } = useWhiteboardStore();

  // Announce tool changes
  useEffect(() => {
    const toolNames: Record<string, string> = {
      pencil: 'Kalem',
      eraser: 'Silgi',
      highlighter: 'Fosforlu kalem',
      selector: 'Seçim aracı',
      laser: 'Lazer işaretçi',
      textReading: 'Metin okuma'
    };

    if (currentTool) {
      announce(`${toolNames[currentTool] || currentTool} aracı seçildi`);
    }
  }, [currentTool, announce]);

  // Announce question changes
  // Note: Question index tracking removed as it's not in the store anymore

  // Listen for custom events
  useEffect(() => {
    const handleUndo = () => announce('İşlem geri alındı');
    const handleRedo = () => announce('İşlem yinelendi');
    const handleClear = () => announce('Tahta temizlendi', 'assertive');
    const handleSave = () => announce('Çizim kaydedildi');
    const handleError = (e: CustomEvent) => announce(e.detail || 'Bir hata oluştu', 'assertive');

    window.addEventListener('undo', handleUndo);
    window.addEventListener('redo', handleRedo);
    window.addEventListener('clear-board', handleClear);
    window.addEventListener('save-success', handleSave);
    window.addEventListener('error-occurred', handleError as any);

    return () => {
      window.removeEventListener('undo', handleUndo);
      window.removeEventListener('redo', handleRedo);
      window.removeEventListener('clear-board', handleClear);
      window.removeEventListener('save-success', handleSave);
      window.removeEventListener('error-occurred', handleError as any);
    };
  }, [announce]);

  // The actual announcer element is created by useAnnounce hook
  return null;
};

// Whiteboard-specific accessibility toolbar
interface A11yToolbarProps {
  onVoiceControl?: () => void;
  onHighContrast?: () => void;
  onTextSize?: (size: 'small' | 'medium' | 'large') => void;
}

export const A11yToolbar: React.FC<A11yToolbarProps> = ({
  onVoiceControl,
  onHighContrast,
  onTextSize
}) => {
  return (
    <div
      role="toolbar"
      aria-label="Erişilebilirlik araçları"
      className="flex items-center space-x-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg"
    >
      {onVoiceControl && (
        <button
          onClick={onVoiceControl}
          aria-label="Sesli kontrol"
          className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        </button>
      )}

      {onHighContrast && (
        <button
          onClick={onHighContrast}
          aria-label="Yüksek kontrast"
          className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </button>
      )}

      {onTextSize && (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onTextSize('small')}
            aria-label="Küçük metin"
            className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            A
          </button>
          <button
            onClick={() => onTextSize('medium')}
            aria-label="Normal metin"
            className="px-2 py-1 text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            A
          </button>
          <button
            onClick={() => onTextSize('large')}
            aria-label="Büyük metin"
            className="px-2 py-1 text-lg rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            A
          </button>
        </div>
      )}
    </div>
  );
};

// Voice commands component
export const VoiceCommands: React.FC = () => {
  const { setCurrentTool } = useWhiteboardStore();
  const announce = useAnnounce();

  // Voice commands mapping
  // Note: These commands would be used with a speech recognition implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _commands = {
    'kalem': () => {
      setCurrentTool('pencil');
      announce('Kalem aracı seçildi');
    },
    'silgi': () => {
      setCurrentTool('eraser');
      announce('Silgi aracı seçildi');
    },
    'fosforlu': () => {
      setCurrentTool('highlighter');
      announce('Fosforlu kalem seçildi');
    },
    'temizle': () => {
      const event = new CustomEvent('clear-board');
      window.dispatchEvent(event);
      announce('Tahta temizlendi');
    },
    'geri al': () => {
      const event = new CustomEvent('undo');
      window.dispatchEvent(event);
      announce('İşlem geri alındı');
    },
    'ileri al': () => {
      const event = new CustomEvent('redo');
      window.dispatchEvent(event);
      announce('İşlem yinelendi');
    },
    'sonraki soru': () => {
      const event = new CustomEvent('next-question');
      window.dispatchEvent(event);
      announce('Sonraki soruya geçildi');
    },
    'önceki soru': () => {
      const event = new CustomEvent('prev-question');
      window.dispatchEvent(event);
      announce('Önceki soruya geçildi');
    }
  };

  return (
    <div className="sr-only" aria-live="polite">
      <h2>Sesli Komutlar</h2>
      <ul>
        <li>Kalem - Kalem aracını seç</li>
        <li>Silgi - Silgi aracını seç</li>
        <li>Fosforlu - Fosforlu kalem seç</li>
        <li>Temizle - Tahtayı temizle</li>
        <li>Geri al - Son işlemi geri al</li>
        <li>İleri al - Geri alınan işlemi yinele</li>
        <li>Sonraki soru - Sonraki soruya geç</li>
        <li>Önceki soru - Önceki soruya geç</li>
      </ul>
    </div>
  );
};